const SDK_SCRIPT_ID = 'facebook-jssdk';
const SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';

let sdkLoadPromise = null;
let currentAppId = null;

/**
 * Loads the Facebook JS SDK once. Does not call FB.init — use ensureFacebookSdk
 * with the correct appId (Messenger vs WhatsApp Business) before FB.login.
 */
export const loadFacebookSdk = () => {
  if (window.FB) {
    return Promise.resolve(window.FB);
  }

  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise((resolve, reject) => {
    const finish = () => {
      if (window.FB) {
        resolve(window.FB);
        return;
      }
      sdkLoadPromise = null;
      reject(new Error('Facebook SDK loaded without FB global'));
    };

    window.fbAsyncInit = () => {
      finish();
    };

    if (!document.getElementById(SDK_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SDK_SCRIPT_ID;
      script.async = true;
      script.src = SDK_SRC;
      script.onerror = () => {
        sdkLoadPromise = null;
        reject(new Error('Failed to load Facebook SDK'));
      };
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        (document.head || document.body || document.documentElement).appendChild(
          script
        );
      }
      return;
    }

    // Script tag already present; wait until FB is available.
    const startedAt = Date.now();
    const poll = () => {
      if (window.FB) {
        finish();
        return;
      }
      if (Date.now() - startedAt > 10000) {
        sdkLoadPromise = null;
        reject(new Error('Facebook SDK load timeout'));
        return;
      }
      window.setTimeout(poll, 50);
    };
    poll();
  });

  return sdkLoadPromise;
};

/**
 * Ensures the SDK is loaded and initialized for the given Meta app.
 * Re-inits when switching between wbAppId (WhatsApp) and fbAppId (Messenger/IG).
 */
export const ensureFacebookSdk = async ({
  appId,
  version,
  cookie = true,
  xfbml = true,
  status = false,
  autoLogAppEvents = true,
} = {}) => {
  if (!appId) {
    throw new Error('Facebook App ID is required');
  }

  const FB = await loadFacebookSdk();
  const nextAppId = String(appId);
  const apiVersion =
    version || window.chatwootConfig?.fbApiVersion || 'v20.0';

  if (currentAppId !== nextAppId) {
    FB.init({
      appId: nextAppId,
      autoLogAppEvents,
      cookie,
      xfbml,
      version: apiVersion,
      status,
    });
    currentAppId = nextAppId;
  }

  return FB;
};

/** @private test helper */
export const resetFacebookSdkState = () => {
  currentAppId = null;
  sdkLoadPromise = null;
};
